// Settings → Admin → Default Organization: company details sent over SSO for
// staff who don't belong to an organization of their own.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  { key: "org_id", label: "Organization ID", claim: "partner_org_id", ph: "org_759fd4157732" },
  { key: "partner_type", label: "Partner type", claim: "partner_type", ph: "Internal" },
  { key: "org_name", label: "Organization name", claim: "partner_org_name", ph: "UnitedCloud Inc." },
];

export default function DefaultOrgPanel({ canEdit }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/default-organization").then((r) => setForm(r.data)).catch(() => setForm({})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/default-organization", {
        org_id: form.org_id || "", partner_type: form.partner_type || "", org_name: form.org_name || "" });
      setForm(data);
      toast.success("Default organization saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (!form) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;
  return (
    <Card className="max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="default-org-panel">
      <div className="mb-1 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold text-slate-800">Default Organization</h3>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        Sent to single sign-on apps for every staff member who isn&apos;t attached to an organization of their own.
      </p>
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label htmlFor={`default-org-${f.key}`}>{f.label} <span className="font-mono text-[11px] text-slate-400">{f.claim}</span></Label>
            <Input id={`default-org-${f.key}`} data-testid={`default-org-${f.key}-input`} value={form[f.key] || ""}
              placeholder={f.ph} disabled={!canEdit} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs text-slate-400" data-testid="default-org-updated">
          {form.updated_at ? `Last saved ${new Date(form.updated_at).toLocaleString()}${form.updated_by_name ? ` by ${form.updated_by_name}` : ""}` : "Not set yet"}
        </span>
        {canEdit && (
          <Button onClick={save} disabled={saving} data-testid="default-org-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        )}
      </div>
    </Card>
  );
}
